import { useCallback, useRef, useState } from 'react';

const CODE_LENGTH = 6;

const useVerificationCode = () => {
  const [inputValues, setInputValues] = useState(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef(Array.from({ length: CODE_LENGTH }, () => null));

  const focusInput = (index) => {
    const clamped = Math.max(0, Math.min(CODE_LENGTH - 1, index));
    inputRefs.current[clamped]?.focus();
  };

  const handleChange = useCallback((index, event) => {
    const digit = event.target.value.replace(/\D/g, '').slice(-1);

    setInputValues((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });

    if (digit) {
      focusInput(index + 1);
    }
  }, []);

  const handleKeyDown = useCallback((index, event) => {
    if (event.key !== 'Backspace') {
      return;
    }

    setInputValues((prev) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = '';
      } else {
        next[Math.max(0, index - 1)] = '';
        focusInput(index - 1);
      }
      return next;
    });
  }, []);

  const handlePaste = useCallback((event) => {
    event.preventDefault();
    const digits = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);

    setInputValues((prev) => {
      const next = [...prev];
      digits.split('').forEach((char, i) => { next[i] = char; });
      return next;
    });

    focusInput(digits.length < CODE_LENGTH ? digits.length : CODE_LENGTH - 1);
  }, []);

  const reset = useCallback(() => {
    setInputValues(Array(CODE_LENGTH).fill(''));
    focusInput(0);
  }, []);

  const code = inputValues.join('');

  return {
    code,
    handleChange,
    handleKeyDown,
    handlePaste,
    inputRefs,
    inputValues,
    isComplete: code.length === CODE_LENGTH,
    reset,
  };
};

export { useVerificationCode };
